import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';

// Define configuration type
interface SegmentConfig {
    writeKey: string;
    cdnURL?: string;
    options?: Record<string, any>;
}

// Define context type
interface SegmentAnalyticsContextType {
    analytics: AnalyticsBrowser | null;
    isLoaded: boolean;
    track: (event: string, properties?: Record<string, any>) => void;
    page: (name?: string, properties?: Record<string, any>) => void;
    identify: (userId: string, traits?: Record<string, any>) => void;
    group: (groupId: string, traits?: Record<string, any>) => void;
    reset: () => void;
}

// Create context with default values
const SegmentAnalyticsContext = createContext<SegmentAnalyticsContextType>({
    analytics: null,
    isLoaded: false,
    track: () => {},
    page: () => {},
    identify: () => {},
    group: () => {},
    reset: () => {},
});

interface SegmentAnalyticsProviderProps {
    children: ReactNode;
    config: SegmentConfig;
    debug?: boolean;
}

export const SegmentAnalyticsProvider: React.FC<SegmentAnalyticsProviderProps> = ({
    children,
    config,
    debug = false,
}) => {
    const [analytics, setAnalytics] = useState<AnalyticsBrowser | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const initAnalytics = async () => {
            try {
                // Initialize analytics with the provided config
                const analyticsInstance = AnalyticsBrowser.load(config);

                if (debug) {
                    console.log('Segment Analytics initialized with config:', config);
                }

                setAnalytics(analyticsInstance);
                setIsLoaded(true);
            } catch (error) {
                console.error('Failed to initialize Segment Analytics:', error);
            }
        };

        initAnalytics().then(() => {});
    }, [config, debug]);

    // Define wrapped functions to handle the case when analytics is not yet loaded
    const track = (event: string, properties?: Record<string, any>) => {
        if (analytics) {
            analytics.track(event, properties);
            if (debug) console.log('Track:', event, properties);
        } else if (debug) {
            console.warn('Analytics not loaded yet. Could not track:', event);
        }
    };

    const page = (name?: string, properties?: Record<string, any>) => {
        if (analytics) {
            analytics.page(name, properties);
            if (debug) console.log('Page:', name, properties);
        } else if (debug) {
            console.warn('Analytics not loaded yet. Could not log page view:', name);
        }
    };

    const identify = (userId: string, traits?: Record<string, any>) => {
        if (analytics) {
            analytics.identify(userId, traits);
            if (debug) console.log('Identify:', userId, traits);
        } else if (debug) {
            console.warn('Analytics not loaded yet. Could not identify user:', userId);
        }
    };

    const group = (groupId: string, traits?: Record<string, any>) => {
        if (analytics) {
            analytics.group(groupId, traits);
            if (debug) console.log('Group:', groupId, traits);
        } else if (debug) {
            console.warn('Analytics not loaded yet. Could not associate group:', groupId);
        }
    };

    const reset = () => {
        if (analytics) {
            analytics.reset();
            if (debug) console.log('Reset analytics');
        } else if (debug) {
            console.warn('Analytics not loaded yet. Could not reset.');
        }
    };

    // Context value
    const contextValue: SegmentAnalyticsContextType = {
        analytics,
        isLoaded,
        track,
        page,
        identify,
        group,
        reset,
    };

    return (
        <SegmentAnalyticsContext.Provider value={contextValue}>
            {children}
        </SegmentAnalyticsContext.Provider>
    );
};

// Custom hook for using Segment Analytics
export const useSegmentAnalytics = () => {
    const context = useContext(SegmentAnalyticsContext);

    if (context === undefined) {
        throw new Error('useSegmentAnalytics must be used within a SegmentAnalyticsProvider');
    }

    return context;
};
